import { useEffect, useRef } from 'react';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';

import type {
  PlayerCameraControlsHandle,
  PlayerCameraInputMode,
} from '../types';
import { usePlayerActions } from './usePlayerActions';

function defaultIgnoreEventWhen(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement;
  if (!target?.tagName) return false;
  const tag = target.tagName.toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export interface UsePlayerHotkeysOptions {
  /**
   * Player/actor id to control. Uses the store's active actor when omitted.
   */
  playerId?: string;
  /**
   * Scope name for hotkeys. When provided and HotkeysProvider is in the tree,
   * this scope is enabled for this player instance.
   */
  scope?: string;
  /**
   * When true, the scope is activated on mount and deactivated on unmount.
   * Set to false to register hotkeys without changing the active scope.
   */
  enableScope?: boolean;
  /**
   * Enables/disables this hook's hotkey handlers.
   */
  enabled?: boolean;
  /**
   * Skip handling the event when this returns true (e.g. when focus is in a slider).
   * Default: ignore when target is input/textarea/select or contenteditable.
   */
  ignoreEventWhen?: (e: KeyboardEvent) => boolean;
  /**
   * Current camera input mode used for temporary Shift-to-pan behavior.
   */
  cameraInputMode?: PlayerCameraInputMode;
  /**
   * Updates the player camera input mode.
   */
  setCameraInputMode?: (mode: PlayerCameraInputMode) => void;
  /**
   * Camera controls handle used for camera-centered hotkeys.
   */
  cameraControls?: PlayerCameraControlsHandle | null;
}

const DEFAULT_SCOPE = 'player';

/**
 * Binds transport hotkeys to the universal player:
 * - Space: play / pause
 * - Left / Right: previous / next frame
 * - Shift + Left / Right: jump 10 frames back / forward
 * - C: frame the active character
 *
 * Ensure HotkeysProvider wraps your app when using scope.
 */
export function usePlayerHotkeys(options: UsePlayerHotkeysOptions = {}) {
  const {
    playerId,
    scope = DEFAULT_SCOPE,
    enableScope = true,
    enabled = true,
    ignoreEventWhen = defaultIgnoreEventWhen,
    cameraInputMode,
    setCameraInputMode,
    cameraControls,
  } = options;

  const { enableScope: enableScopeContext, disableScope } = useHotkeysContext();
  const actions = usePlayerActions(playerId);
  const inputModeBeforeShiftRef = useRef<PlayerCameraInputMode | null>(null);

  useEffect(() => {
    if (!enabled || !enableScope || !scope) return;
    enableScopeContext(scope);
    return () => disableScope(scope);
  }, [scope, enabled, enableScope, enableScopeContext, disableScope]);

  useHotkeys('space', () => actions.playOrPause(), {
    preventDefault: true,
    scopes: [scope],
    enabled,
  });

  useHotkeys(Key.ArrowLeft, () => actions.frameJump(-1), {
    preventDefault: true,
    ignoreEventWhen,
    scopes: [scope],
    enabled,
  });
  useHotkeys(Key.ArrowRight, () => actions.frameJump(1), {
    preventDefault: true,
    ignoreEventWhen,
    scopes: [scope],
    enabled,
  });
  useHotkeys(`shift+${Key.ArrowLeft}`, () => actions.frameJump(-10), {
    preventDefault: true,
    ignoreEventWhen,
    scopes: [scope],
    enabled,
  });
  useHotkeys(`shift+${Key.ArrowRight}`, () => actions.frameJump(10), {
    preventDefault: true,
    ignoreEventWhen,
    scopes: [scope],
    enabled,
  });
  useHotkeys(
    'c',
    () => {
      if (!cameraControls?.frame) {
        return;
      }

      cameraControls.frame(true);
    },
    {
      preventDefault: true,
      ignoreEventWhen,
      scopes: [scope],
      enabled: enabled && Boolean(cameraControls?.frame),
    },
  );

  useHotkeys(
    'shift',
    (keyboardEvent) => {
      if (!setCameraInputMode || !cameraInputMode) {
        return;
      }

      if (keyboardEvent.type === 'keydown') {
        if (inputModeBeforeShiftRef.current) {
          return;
        }

        inputModeBeforeShiftRef.current = cameraInputMode;
        setCameraInputMode('truck');
        return;
      }

      if (inputModeBeforeShiftRef.current) {
        setCameraInputMode(inputModeBeforeShiftRef.current);
        inputModeBeforeShiftRef.current = null;
      }
    },
    {
      preventDefault: true,
      keydown: true,
      keyup: true,
      scopes: [scope],
      enabled,
    },
  );
}
